import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { api, onSessionRejected, setSession } from '@/lib/api';
import { useAuth } from '@/lib/auth/useAuth';
import { ApplicantSessionContext, type ApplicantSessionState, type ApplicantView } from './ApplicantSessionContext';
import { APPLICANT_SESSION_KEY } from './storage';

function stored(): string | null {
  try {
    return sessionStorage.getItem(APPLICANT_SESSION_KEY);
  } catch {
    return null;
  }
}

function remember(token: string | null): void {
  setSession(token);
  try {
    if (token) sessionStorage.setItem(APPLICANT_SESSION_KEY, token);
    else sessionStorage.removeItem(APPLICANT_SESSION_KEY);
  } catch {
    // Storage unavailable: the session works for this page and not after a reload.
  }
}

/**
 * An applicant's session: a code emailed to them, exchanged for a token.
 *
 * Beside the staff AuthProvider rather than inside it. The two are different
 * people with different authorities (CLAUDE.md, constraint 3), and the backend
 * refuses each the other's endpoints; one tab holds one of them at a time. Signing
 * in as either ends the other.
 */
export function ApplicantSessionProvider({ children }: { children: ReactNode }) {
  const { me: staff, signOut: signOutStaff } = useAuth();
  const [applicant, setApplicant] = useState<ApplicantView | null>(null);
  const [loading, setLoading] = useState(() => stored() !== null);

  // Local only: for a session the server has already refused.
  const forget = useCallback(() => {
    remember(null);
    setApplicant(null);
  }, []);

  // Signing out ends the applicant's sessions on every device: a passport left
  // open on a shared computer closes with it.
  const signOut = useCallback(() => {
    api.end('/api/applicant/sign-out');
    forget();
  }, [forget]);

  useEffect(() => onSessionRejected(forget), [forget]);

  useEffect(() => {
    const token = stored();
    if (token === null) return;
    setSession(token);
    api
      .get<ApplicantView>('/api/applicant/me')
      .then(setApplicant)
      .catch(() => remember(null))
      .finally(() => setLoading(false));
  }, []);


  const requestCode = useCallback(async (email: string) => {
    await api.post('/api/applicant/sign-in/code', { email });
  }, []);

  const signIn = useCallback(
    async (email: string, code: string) => {
      const session = await api.post<{ token: string; applicant: ApplicantView }>(
        '/api/applicant/sign-in',
        { email, code },
      );
      if (staff !== null) signOutStaff();
      remember(session.token);
      setApplicant(session.applicant);
    },
    [staff, signOutStaff],
  );

  // A member of staff signed in in this tab: whatever applicant session there was
  // has been replaced (AuthProvider clears it), so it is not presented.
  const current = staff === null ? applicant : null;

  const value = useMemo<ApplicantSessionState>(
    () => ({ applicant: current, loading, requestCode, signIn, signOut }),
    [current, loading, requestCode, signIn, signOut],
  );
  return <ApplicantSessionContext value={value}>{children}</ApplicantSessionContext>;
}
